/**
 * MedicationCard (CONTRACTS §6) — due-medication summary for the current
 * client, reused by the Today page and the client agenda.
 *
 * Self-contained: loads its own occurrences via
 * repositories.medication.listOccurrences(date, date) for the signed-in
 * client and is safe to render with zero props.
 *
 * Props (all optional):
 * - date: local ISO date, defaults to today;
 * - readOnly: hides every mutating action (caregiver views).
 *
 * Actions (client-only, role comes from the server profile via useAuth):
 * 'Ingenomen' per due occurrence with optional short note, bounded undo
 * (30-minute product policy), and the proof upload entry point.
 */
import { useState } from 'react'
import { Button, Card, Dialog, EmptyState, ErrorState, Skeleton, TextArea } from '@/components/ui'
import { PillIcon, VideoIcon } from '@/components/icons'
import { useAuth } from '@/features/auth'
import type { MedicationIntakeEvent } from '@/types'
import { canAttachProof, canMarkTaken } from './statuses'
import { todayLocalISODate } from './dates'
import {
  isUndoAllowed,
  undoMinutesRemaining,
  useMedicationDay,
  type OccurrenceItem,
} from './useMedicationData'
import { MedicationGroupList, occurrenceTitle } from './MedicationGroupList'
import { ProofCaptureSheet } from './proof/ProofCaptureSheet'
import './medication.css'

export type MedicationCardProps = {
  /** Local ISO date (yyyy-mm-dd). Defaults to today. */
  date?: string
  /** Hides all mutating actions (caregiver / shared views). */
  readOnly?: boolean
}

export function MedicationCard({ date, readOnly = false }: MedicationCardProps) {
  const effectiveDate = date ?? todayLocalISODate()
  const { role } = useAuth()
  const { status, items, error, busyAction, refresh, markTaken, undoTaken } = useMedicationDay(effectiveDate)

  const [noteTarget, setNoteTarget] = useState<OccurrenceItem | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteError, setNoteError] = useState<string | null>(null)
  const [undoTarget, setUndoTarget] = useState<MedicationIntakeEvent | null>(null)
  const [proofTarget, setProofTarget] = useState<OccurrenceItem | null>(null)

  // Mutating actions are client-only; a caregiver always gets the read-only view.
  const actionsEnabled = !readOnly && role === 'client'

  const closeNoteDialog = () => {
    setNoteTarget(null)
    setNoteText('')
    setNoteError(null)
  }

  const confirmTaken = async () => {
    if (!noteTarget) return
    try {
      await markTaken(noteTarget.occurrence.id, noteText)
      closeNoteDialog()
    } catch {
      setNoteError('Innemen is niet gelukt. Probeer het opnieuw.')
    }
  }

  const confirmUndo = async () => {
    if (!undoTarget) return
    try {
      await undoTaken(undoTarget.id)
      setUndoTarget(null)
    } catch {
      setUndoTarget(null)
      await refresh()
    }
  }

  if (status === 'loading') {
    return <Skeleton lines={3} />
  }

  if (status === 'unavailable') {
    return (
      <Card padding="md" className="medication-card">
        <p className="medication-muted">
          Cloud niet ingesteld. Je medicatiemomenten kunnen nu niet worden opgehaald.
        </p>
      </Card>
    )
  }

  if (status === 'error') {
    return <ErrorState error={error ?? undefined} onRetry={() => void refresh()} />
  }

  return (
    <Card padding="sm" className="medication-card">
      {items.length === 0 ? (
        <EmptyState
          icon={<PillIcon size={24} />}
          title="Geen medicatie gepland"
          body="Voor deze dag staan er geen medicatiemomenten in het schema."
        />
      ) : (
        <MedicationGroupList
          items={items}
          renderActions={
            actionsEnabled
              ? (item) => {
                  const intake = item.intakeEvent
                  const undoable = intake !== null && isUndoAllowed(intake)
                  return (
                    <>
                      {canMarkTaken(item.occurrence.status) ? (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={busyAction}
                          onClick={() => {
                            setNoteTarget(item)
                            setNoteText('')
                            setNoteError(null)
                          }}
                        >
                          Ingenomen
                        </Button>
                      ) : null}
                      {canAttachProof(item.occurrence.status) && intake ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busyAction}
                          onClick={() => setProofTarget(item)}
                        >
                          <VideoIcon size={16} />
                          Bewijsvideo
                        </Button>
                      ) : null}
                      {undoable && intake ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyAction}
                          onClick={() => setUndoTarget(intake)}
                        >
                          Ongedaan maken (nog {undoMinutesRemaining(intake)} min)
                        </Button>
                      ) : null}
                    </>
                  )
                }
              : undefined
          }
        />
      )}

      {/* Optional short note when marking taken */}
      <Dialog
        open={noteTarget !== null}
        onClose={closeNoteDialog}
        title="Medicatie ingenomen"
        footer={
          <div className="medication-dialog-footer">
            <Button variant="ghost" onClick={closeNoteDialog} disabled={busyAction}>
              Annuleren
            </Button>
            <Button variant="primary" loading={busyAction} onClick={() => void confirmTaken()}>
              Bevestigen
            </Button>
          </div>
        }
      >
        {noteTarget ? (
          <div className="medication-dialog-body">
            <p className="medication-muted">
              {occurrenceTitle(noteTarget)} · {noteTarget.occurrence.localTime}
            </p>
            <TextArea
              label="Korte notitie (optioneel)"
              value={noteText}
              onChange={setNoteText}
              rows={3}
              placeholder="Bijvoorbeeld: met water ingenomen"
            />
            {noteError ? (
              <p className="medication-error" role="alert">
                {noteError}
              </p>
            ) : null}
            <p className="medication-muted">
              Je kunt dit binnen 30 minuten ongedaan maken. Daarna blijft de inname vastgelegd.
            </p>
          </div>
        ) : null}
      </Dialog>

      {/* Bounded undo confirmation */}
      <Dialog
        open={undoTarget !== null}
        onClose={() => setUndoTarget(null)}
        title="Inname ongedaan maken"
        footer={
          <div className="medication-dialog-footer">
            <Button variant="ghost" onClick={() => setUndoTarget(null)} disabled={busyAction}>
              Terug
            </Button>
            <Button variant="danger" loading={busyAction} onClick={() => void confirmUndo()}>
              Ongedaan maken
            </Button>
          </div>
        }
      >
        <p className="medication-muted">
          De registratie ‘ingenomen’ wordt verwijderd en het moment komt weer open te staan. Dit kan
          alleen binnen 30 minuten na het innemen.
        </p>
      </Dialog>

      {/* Proof video entry point (consent always first; privacy gate when disabled) */}
      {proofTarget?.intakeEvent ? (
        <ProofCaptureSheet
          open={proofTarget !== null}
          onClose={() => setProofTarget(null)}
          intakeEventId={proofTarget.intakeEvent.id}
          contextLabel={`${occurrenceTitle(proofTarget)} · ${proofTarget.occurrence.localTime}`}
          onUploaded={() => {
            void refresh()
          }}
        />
      ) : null}
    </Card>
  )
}
